/**
 * t369u: per-workspace modules. A company can switch a whole area of The Hub off (say it doesn't manage shifts): its
 * pages, tabs, Home widgets, quick actions, notifications, reminders, search results, help articles, settings and
 * role-editor rows are hidden, and the API refuses its endpoints (403 `module_off`). Nothing is deleted: switching it
 * back on brings everything back. Everything is on by default.
 *
 * This file is the one list. The API (ModuleGuard, notifications, workers, assistant, search) and the web and iPhone
 * apps (nav, tabs, More, ＋, widgets, settings, help) all read it. Pure TS: no DOM, no Node.
 *
 * Stored in collab.workspaces.settings: `modules` (the switches, only the ones turned off are kept) and `modules_off`
 * (the effective list, dependencies applied, which SQL in the worker scans reads through collab.module_on()).
 * The AI assistant's switch is the existing `ai.assistant` setting (Settings › AI & translation), so there is one.
 */

export type ModuleId =
  | 'announcements' | 'kudos' | 'events' | 'calls' | 'polls'
  | 'shifts' | 'timeoff' | 'timeclock' | 'reports' | 'training' | 'hr'
  | 'tasks' | 'issues' | 'checklists' | 'equipment' | 'visitors' | 'handovers' | 'forms' | 'knowledge' | 'policies'
  | 'fleet' | 'sales' | 'assistant';

export type ModuleArea = 'home' | 'chat' | 'shifts' | 'operations' | 'more';
export const MODULE_AREAS: { id: ModuleArea; title: string }[] = [
  { id: 'home', title: 'Home & chat' },
  { id: 'shifts', title: 'Shifts & people' },
  { id: 'operations', title: 'Operations' },
  { id: 'more', title: 'Fleet, sales & AI' },
];

export interface ModuleDef {
  id: ModuleId;
  label: string;
  /** One muted line under the switch. */
  description: string;
  area: ModuleArea;
  /** Off when any of these is off (the switch stays as set, so turning the other back on restores it). */
  requires?: ModuleId[];
  /** Web/app page paths (prefix match on whole segments; "*" matches one segment). */
  routes: string[];
  /** API paths after /workspaces/:ws/ (prefix match on whole segments). */
  api: string[];
  /** Permission keys (exact) or prefixes ending in "." that belong to it (hidden in the role editor). */
  perms: string[];
  /** Notification kinds (prefix match). */
  notifications: string[];
  /** Home widget keys. */
  widgets: string[];
  /** Help Centre guide areas. */
  help: string[];
  /** Settings sections ("<group>/<item>"). */
  settings: string[];
  /** Search result kinds (GET /search: SEARCH_TYPES). */
  search: string[];
  /** Calendar layers it brings. */
  calendar: string[];
}

const M = (d: Partial<ModuleDef> & Pick<ModuleDef, 'id' | 'label' | 'description' | 'area'>): ModuleDef =>
  ({ routes: [], api: [], perms: [], notifications: [], widgets: [], help: [], settings: [], search: [], calendar: [], ...d });

export const MODULES: ModuleDef[] = [
  // Home & chat
  M({ id: 'announcements', area: 'home', label: 'Announcements', description: 'Company news with read receipts',
    routes: ['/announcements', '/announcement'], api: ['announcements', 'threads/announcement'], perms: ['comms.announcement.'], notifications: ['announcement.'], widgets: ['announcements'], search: ['announcements'] }),
  M({ id: 'kudos', area: 'home', label: 'Kudos & celebrations', description: 'Thanks between colleagues, badges and birthday posts',
    routes: ['/kudos'], api: ['kudos', 'threads/kudos'], perms: ['kudos.'], notifications: ['people.kudos', 'people.birthday', 'kudos.'], widgets: ['kudos', 'birthdays'], help: ['kudos'],
    settings: ['engagement/kudos', 'engagement/celebrations'], calendar: ['birthdays'] }),
  M({ id: 'events', area: 'home', label: 'Calendar & events', description: 'Company, team and personal events with invites',
    routes: ['/calendar', '/event'], api: ['calendar/events', 'calendar/settings', 'calendar/feed', 'calendar/permissions', 'threads/calendar_event'], perms: ['calendar.event.'], notifications: ['calendar.'], widgets: ['agenda'],
    help: ['calendar'], settings: ['account/calendar'], search: ['events'], calendar: ['company', 'team', 'building', 'personal'] }),
  M({ id: 'calls', area: 'home', label: 'Calls & ad-hoc meeting rooms', description: 'Voice and video calls, ad-hoc meeting rooms and meeting notes',
    routes: ['/meeting-rooms', '/rooms', '/call', '/closed-rooms'], api: ['calls', 'rooms'], perms: ['chat.room.', 'chat.call.', 'ai.meeting_notes.'], notifications: ['call.'] }),
  M({ id: 'polls', area: 'home', label: 'Polls', description: 'Polls and surveys in channels',
    perms: ['chat.poll.'], notifications: ['poll.'] }),

  // Shifts & people
  M({ id: 'shifts', area: 'shifts', label: 'Shifts & rota', description: 'Rota, my shifts, open shifts, swaps and availability',
    routes: ['/workforce/my-shifts', '/workforce/schedule', '/workforce/offers', '/rota', '/shifts', '/shift', '/shifts-team', '/shifts-availability'],
    api: ['workforce/shifts', 'workforce/schedule', 'workforce/open-shifts', 'workforce/shift-offers', 'workforce/swaps', 'workforce/availability', 'workforce/shift-templates',
      'workforce/shift-series', 'workforce/rota-templates', 'workforce/team-schedule', 'workforce/my-shifts', 'workforce/shift-messages', 'workforce/claims', 'rooms/shift-groups', 'threads/shift_offer'],
    perms: ['workforce.rota.', 'workforce.openshift.', 'workforce.swap.', 'workforce.availability.', 'buildings.view_shifts'],
    notifications: ['workforce.shift_', 'workforce.open_shift', 'workforce.swap_', 'workforce.schedule_published', 'workforce.rota_change'],
    widgets: ['shifts'], help: ['schedule'], settings: ['shifts/policy', 'shifts/presets', 'shifts/time-rules'], calendar: ['shifts'] }),
  M({ id: 'timeoff', area: 'shifts', label: 'Time off', description: 'Holiday and leave requests, balances and approvals',
    routes: ['/workforce/time-off', '/time-off'], api: ['workforce/leave', 'workforce/leave-types', 'workforce/holiday'],
    perms: ['workforce.leave.', 'calendar.absence.'], notifications: ['workforce.leave_'], help: ['leave'], calendar: ['absences'] }),
  M({ id: 'timeclock', area: 'shifts', label: 'Time clock & timesheets', description: 'Clock in and out, kiosks, timesheets and payroll export', requires: ['shifts'],
    routes: ['/workforce/timesheets', '/clock'], api: ['workforce/time', 'workforce/timesheets', 'workforce/attendance', 'workforce/kiosks', 'workforce/kiosk-credentials', 'workforce/kiosk-events', 'workforce/geofences', 'workforce/export'],
    perms: ['workforce.timesheet.', 'workforce.clock.', 'workforce.payroll.'], notifications: ['workforce.still_clocked_in', 'workforce.missed_clock_in', 'workforce.auto_clocked_out', 'workforce.timesheet_'],
    widgets: ['clock'], help: ['time-clock'], settings: ['shifts/clock-in', 'account/kiosk'] }),
  M({ id: 'reports', area: 'shifts', label: 'Workforce reports', description: 'Hours, labour cost, attendance and staffing reports', requires: ['shifts'],
    routes: ['/workforce/reports'], api: ['workforce/reports'], perms: ['workforce.reports.'], widgets: ['kpi_hours'], help: ['reports'] }),
  M({ id: 'training', area: 'shifts', label: 'Training & onboarding', description: 'Courses with quizzes and new starter checklists', requires: ['knowledge'],
    routes: ['/training', '/onboarding'], api: ['training', 'onboarding'], perms: ['workforce.onboarding.'], notifications: ['training.', 'onboarding.'],
    widgets: ['training', 'onboarding'], settings: ['operations/onboarding-templates'] }),
  M({ id: 'hr', area: 'shifts', label: 'HR records', description: 'Personal, bank, tax and emergency details, documents and right to work',
    routes: ['/hr', '/settings/people/hr', '/settings/people/hr-expiring', '/settings/people/hr-changes', '/settings/people/hr-reports', '/settings/people/hr-missing', '/settings/account/hr'], api: ['hr'], perms: ['hr.'], notifications: ['hr.'], widgets: ['hr_approvals'], settings: ['people/hr', 'people/hr-expiring', 'people/hr-changes', 'people/hr-reports', 'people/hr-missing', 'account/hr'] }),

  // Operations
  M({ id: 'tasks', area: 'operations', label: 'Tasks & projects', description: 'Tasks, private tasks, projects and boards',
    routes: ['/tasks', '/projects', '/todos', '/task', '/all-tasks'], api: ['tasks', 'my-tasks', 'projects', 'task-areas'], perms: ['tasks.'], notifications: ['task.', 'todo.', 'building.task'],
    widgets: ['tasks', 'projects'], help: ['tasks'], search: ['tasks'], calendar: ['tasks', 'todos'] }),
  M({ id: 'issues', area: 'operations', label: 'Issues', description: 'Report problems, maintenance and incidents with SLAs',
    routes: ['/incidents', '/buildings/*/issues', '/issues', '/issue', '/all-issues', '/report'], api: ['incidents', 'scoped-forms/incident', 'threads/incident'], perms: ['ops.incident.'], notifications: ['ops.incident'],
    widgets: ['incidents', 'kpi_incidents'], help: ['issues'], settings: ['operations/issue-form', 'operations/issue-settings'], search: ['issues'] }),
  M({ id: 'checklists', area: 'operations', label: 'Checklists', description: 'Opening, closing and safety checklists on a schedule',
    routes: ['/checklists', '/tasks/checklists', '/checklist', '/checklist-template'], api: ['checklists'], perms: ['ops.checklist.'], notifications: ['ops.checklist'],
    widgets: ['checklists'], settings: ['operations/checklist-templates', 'operations/checklist-schedules'] }),
  M({ id: 'equipment', area: 'operations', label: 'Equipment', description: 'Equipment register, pre-use checks, services and inspections', requires: ['issues', 'checklists'],
    routes: ['/equipment'], api: ['equipment', 'equipment-categories', 'equipment-inspection-types', 'threads/equipment'], notifications: ['ops.equipment'], settings: ['operations/equipment-categories', 'operations/equipment-inspection-types'] }),
  M({ id: 'visitors', area: 'operations', label: 'Visitors & on site', description: 'Visitor sign-in, who is on site and the fire roll call',
    routes: ['/visitors'], api: ['workforce/visitors', 'workforce/visitor-passes', 'workforce/roll-calls'], notifications: ['workforce.visitor_', 'workforce.roll_call'] }),
  M({ id: 'handovers', area: 'operations', label: 'Handovers', description: 'Notes from one shift to the next', requires: ['shifts'],
    routes: ['/workforce/handovers', '/workforce/handover-templates', '/handovers', '/handover'], api: ['workforce/handovers', 'scoped-forms/handover'], perms: ['workforce.handover.'],
    notifications: ['workforce.handover'], widgets: ['handovers'], help: ['handovers'], settings: ['operations/handover-templates'] }),
  M({ id: 'forms', area: 'operations', label: 'Channel forms', description: 'Form builder and form channels for requests',
    routes: ['/forms'], api: ['form-templates'], perms: ['forms.'], notifications: ['form.'], help: ['forms'], settings: ['operations/forms'] }),
  M({ id: 'knowledge', area: 'operations', label: 'Knowledge base', description: 'Procedures, how-tos and team know-how',
    routes: ['/kb', '/help/kb'], api: ['kb'], perms: ['kb.'], notifications: ['kb.'], settings: ['operations/kb-templates'], search: ['articles'] }),

  // t563u: policy and document management (versions, approvals, acknowledgements). Separate from the knowledge base.
  M({ id: 'policies', area: 'operations', label: 'Policies', description: 'Policies and procedures with versions, approvals and acknowledgements',
    routes: ['/policies'], api: ['policies'], perms: ['policies.'], notifications: ['policy.'], help: ['policies'], settings: ['operations/policy-types', 'operations/policy-blocks'],
    search: ['policies'] }),

  // Fleet, sales & AI
  M({ id: 'fleet', area: 'more', label: 'Fleet & deliveries', description: 'Vehicles, drivers, journeys and driver runs',
    routes: ['/fleet', '/runs', '/run'], api: ['fleet', 'threads/journey'], perms: ['fleet.'], notifications: ['fleet.'], help: ['fleet'],
    settings: ['fleet/vehicles', 'fleet/drivers', 'fleet/hauliers', 'fleet/settings'] }),
  M({ id: 'sales', area: 'more', label: 'Sales', description: 'Till takings, targets and bonuses from Odoo',
    routes: ['/sales'], api: ['sales'], perms: ['sales.'], widgets: ['sales_takings', 'sales_transactions', 'sales_basket', 'sales_top'], settings: ['integrations/odoo'] }),
  M({ id: 'assistant', area: 'more', label: 'AI assistant', description: 'Ask questions and get help from the assistant',
    routes: ['/assistant', '/ask'], api: ['assistant'], perms: ['assistant.', 'ai.assistant.'] }),
];

export const MODULE_IDS: ModuleId[] = MODULES.map(m => m.id);
export const MODULE_BY_ID = new Map(MODULES.map(m => [m.id, m]));
export const moduleLabel = (id: ModuleId) => MODULE_BY_ID.get(id)?.label ?? id;

/** Who may switch modules: the same workspace-wide permission as roles (owners and admins). */
export const MODULES_PERMISSION = 'admin.roles.manage';

export type ModuleSwitches = Record<ModuleId, boolean>;
export interface WorkspaceModules {
  /** The switches as set. */
  switches: ModuleSwitches;
  /** Effective: a module is off when its switch is off or a module it needs is off. */
  effective: ModuleSwitches;
  /** Effective off list (what everything checks). */
  off: ModuleId[];
}

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const allOn = (): ModuleSwitches => Object.fromEntries(MODULE_IDS.map(id => [id, true])) as ModuleSwitches;

/** Dependencies applied (transitively), in catalogue order. */
export function effectiveModules(switches: Partial<ModuleSwitches>): ModuleSwitches {
  const out = { ...allOn(), ...switches } as ModuleSwitches;
  for (let changed = true; changed;) {
    changed = false;
    for (const m of MODULES) if (out[m.id] && m.requires?.some(r => !out[r])) { out[m.id] = false; changed = true; }
  }
  return out;
}

/** Settings jsonb → modules. Unknown keys are ignored; the assistant follows `ai.assistant`. */
export function readModules(settings: unknown): WorkspaceModules {
  const s = isObj(settings) ? settings : {};
  const raw = isObj(s.modules) ? s.modules : {};
  const switches = allOn();
  for (const id of MODULE_IDS) if (raw[id] === false) switches[id] = false;
  if (isObj(s.ai) && s.ai.assistant === false) switches.assistant = false;
  const effective = effectiveModules(switches);
  return { switches, effective, off: MODULE_IDS.filter(id => !effective[id]) };
}

/** Modules that need this one (turned off with it): "Time clock & timesheets" for Shifts. */
export const dependentsOf = (id: ModuleId): ModuleId[] => MODULES.filter(m => m.requires?.includes(id)).map(m => m.id);

/** Validate a switch change: { <module>: boolean, ... }. */
export function validateModulesPatch(patch: unknown): { value: Partial<ModuleSwitches>; errors: string[] } {
  const value: Partial<ModuleSwitches> = {};
  const errors: string[] = [];
  if (!isObj(patch)) return { value, errors: ['Send an object of modules'] };
  for (const [k, v] of Object.entries(patch)) {
    if (!MODULE_BY_ID.has(k as ModuleId)) { errors.push(`Unknown module "${k}"`); continue; }
    if (typeof v !== 'boolean') { errors.push(`${k} must be true or false`); continue; }
    value[k as ModuleId] = v;
  }
  return { value, errors };
}

// ------------------------------------------------------------------------------------------------ lookups
type Off = readonly ModuleId[] | ReadonlySet<ModuleId> | null | undefined;
const toSet = (off: Off): ReadonlySet<ModuleId> => (off instanceof Set ? off : new Set(off ?? []));
export const isModuleOn = (off: Off, id: ModuleId | null | undefined) => !id || !toSet(off).has(id);

const clean = (p: string) => p.split(/[?#]/)[0].replace(/\/+$/, '') || '/';
/** Whole-segment prefix match; "*" in the pattern matches one segment. Returns the pattern's length or -1. */
function segMatch(path: string, pattern: string): number {
  const a = clean(path).split('/');
  const b = clean(pattern).split('/');
  if (b.length > a.length) return -1;
  for (let i = 0; i < b.length; i++) if (b[i] !== '*' && b[i] !== a[i]) return -1;
  return pattern.length;
}
/** Longest match wins, so /tasks/checklists is Checklists while /tasks is Tasks. */
function bestModule(path: string, pick: (m: ModuleDef) => string[]): ModuleId | null {
  let best: { id: ModuleId; len: number } | null = null;
  for (const m of MODULES) for (const p of pick(m)) {
    const len = segMatch(path, p);
    if (len >= 0 && (!best || len > best.len)) best = { id: m.id, len };
  }
  return best?.id ?? null;
}

/** The module a page belongs to (web route or iPhone path), or null for core pages (Home, Chat, People, Settings…). */
export function moduleForPath(path: string): ModuleId | null {
  const p = path.startsWith('/') ? path : `/${path}`;
  // The approvals inbox under Rota also holds time off and timesheets, so it stays open with Shifts off.
  if (segMatch(p, '/rota/approvals') >= 0) return null;
  return bestModule(p, m => m.routes);
}
/** Is the page available (its module on)? Core pages always are. */
export const pathAvailable = (off: Off, path: string) => isModuleOn(off, moduleForPath(path));

/** The module an API path belongs to. `rest` is the part after /workspaces/:ws/ (no leading slash). */
export const moduleForApi = (rest: string): ModuleId | null => bestModule(`/${rest.replace(/^\/+/, '')}`, m => m.api.map(a => `/${a}`));

export function moduleForPermission(key: string): ModuleId | null {
  for (const m of MODULES) if (m.perms.some(p => (p.endsWith('.') ? key.startsWith(p) : key === p))) return m.id;
  return null;
}
export function moduleForNotification(kind: string): ModuleId | null {
  let best: { id: ModuleId; len: number } | null = null;
  for (const m of MODULES) for (const p of m.notifications) if (kind.startsWith(p) && (!best || p.length > best.len)) best = { id: m.id, len: p.length };
  return best?.id ?? null;
}
/** Notification kind prefixes of switched-off modules, as SQL LIKE patterns (the bell hides them; rows are kept). */
export function notificationPatternsOff(off: Off): string[] {
  const set = toSet(off);
  return MODULES.filter(m => set.has(m.id)).flatMap(m => m.notifications.map(p => `${p.replace(/[\\%_]/g, c => `\\${c}`)}%`));
}
const firstWith = (pick: (m: ModuleDef) => string[]) => (key: string): ModuleId | null => MODULES.find(m => pick(m).includes(key))?.id ?? null;
export const moduleForWidget = firstWith(m => m.widgets);
export const moduleForHelpArea = firstWith(m => m.help);
export const moduleForSearchType = firstWith(m => m.search);
export const moduleForCalendarLayer = firstWith(m => m.calendar);
/** Settings sections: by "<group>/<item>". Shared rows (QR codes) go only when all their modules are off. */
const SHARED_SETTINGS: Record<string, ModuleId[]> = { 'operations/qr-points': ['issues', 'checklists', 'fleet'] };
export function settingsSectionAvailable(off: Off, groupItem: string): boolean {
  const set = toSet(off);
  const shared = SHARED_SETTINGS[groupItem];
  if (shared) return shared.some(id => !set.has(id));
  const id = MODULES.find(m => m.settings.includes(groupItem))?.id;
  return !id || !set.has(id);
}
/** A switched-off module whose settings page `path` (/settings/<group>/<item>) is, or null when the page is available. */
export function settingsPathModuleOff(off: Off, path: string): ModuleId | null {
  const m = /^\/settings\/([a-z-]+)\/([a-z-]+)/.exec(clean(path));
  if (!m || settingsSectionAvailable(off, `${m[1]}/${m[2]}`)) return null;
  const key = `${m[1]}/${m[2]}`;
  return SHARED_SETTINGS[key]?.[0] ?? MODULES.find(x => x.settings.includes(key))?.id ?? null;
}

/** Home feed filters (FEED_TYPES) that come from a module. */
export const FEED_TYPE_MODULE: Record<string, ModuleId> = {
  announcements: 'announcements', kudos: 'kudos', birthdays: 'kudos', offers: 'shifts', tasks: 'tasks', issues: 'issues', events: 'events',
};
/** Entity types (threads, notifications, feed discussions) that belong to a module. */
export const ENTITY_TYPE_MODULE: Record<string, ModuleId> = {
  announcement: 'announcements', kudos: 'kudos', calendar_event: 'events', task: 'tasks', project: 'tasks', incident: 'issues',
  shift_offer: 'shifts', shift: 'shifts', equipment: 'equipment', poll: 'polls', checklist_run: 'checklists', handover: 'handovers',
  leave_request: 'timeoff', time_entry: 'timeclock', journey: 'fleet', training_course: 'training', kb_article: 'knowledge', call: 'calls',
};

/** Notification settings rows (notificationMatrix groups): hidden when every module they cover is off. */
export const NOTIFICATION_GROUP_MODULES: Record<string, ModuleId[]> = {
  my_rota: ['shifts'], open_shifts: ['shifts'], rota_changes: ['shifts'], shifts: ['shifts', 'timeoff', 'timeclock', 'handovers', 'visitors'],
  issues: ['issues', 'equipment'], checklists: ['checklists'], tasks: ['tasks', 'forms'], kudos: ['kudos'], fleet: ['fleet'], knowledge: ['knowledge', 'training', 'policies'],
  calendar: ['events'], hr: ['hr'], hr_expiry: ['hr'],
};
export function notificationGroupAvailable(off: Off, group: string): boolean {
  const ids = NOTIFICATION_GROUP_MODULES[group];
  if (!ids) return true;
  const set = toSet(off);
  return ids.some(id => !set.has(id));
}

/** Is a permission shown (role editor, access checker)? Permissions of switched-off modules are hidden, grants kept. */
export const permissionAvailable = (off: Off, key: string) => isModuleOn(off, moduleForPermission(key));
/** Help guide article: its area's module, then the pages it explains (all of them off hides it). */
export function helpArticleAvailable(off: Off, a: { area: string; routes?: readonly string[] }): boolean {
  if (!isModuleOn(off, moduleForHelpArea(a.area))) return false;
  const routes = (a.routes ?? []).map(r => r.replace(/\/\*$/, '').replace(/:[^/]+/g, '*')).filter(r => r && r !== '/');
  return !routes.length || routes.some(r => pathAvailable(off, r));
}

/** The calm "not available" wording a disabled page shows. */
export const MODULE_OFF_TITLE = 'Not available';
export const moduleOffMessage = (id: ModuleId | null) => `${id ? moduleLabel(id) : 'This'} is turned off for your workspace.`;
/** The API's error code when a module is off. */
export const MODULE_OFF_CODE = 'module_off';

/** t369u: a calendar layer shows (in the layer pickers) only while its module is on. */
export const calendarLayerAvailable = (off: Off, layer: string) => isModuleOn(off, moduleForCalendarLayer(layer));
